// message.js
import axios from "../helpers/axios";
import { chatConstants, messageConstants } from "./constants";

const getId = (value) => String(value?._id || value || "");

const createClientMessageId = () => (
    `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

const normalizeTimelineItems = (items = [], chatId) => {
    return items
        .filter(Boolean)
        .map((item) => ({
            ...item,
            chatId: item.chatId || chatId,
        }));
};


export const sendMessage = (chatId, messageData = {}, file = null) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const formData = new FormData();

            formData.append("text", messageData.text || "");

            if (messageData.sender) formData.append("sender", messageData.sender);
            if (messageData.messageKind) formData.append("messageKind", messageData.messageKind);
            if (messageData.mediaDurationSec != null) formData.append("mediaDurationSec", String(messageData.mediaDurationSec));
            if (messageData.replyTo) formData.append("replyTo", JSON.stringify(messageData.replyTo));
            if (messageData.entity) formData.append("entity", JSON.stringify(messageData.entity));
            if (file) formData.append("file", file);

            const res = await axios.post(`/messages/${chatId}`, formData, {
                onUploadProgress: (event) => {
                    if (!file || !event?.total) return;

                    const progress = Math.round((event.loaded * 100) / event.total);
                    dispatch({
                        type: messageConstants.SEND_MESSAGE_PROGRESS,
                        payload: { progress },
                    });
                },
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({
                    type: messageConstants.SEND_MESSAGE_SUCCESS,
                    payload: { message },
                });


                return message;
            }

            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: "Сообщение не создано" },
            });

            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const sendTextMessage = (
    chatId,
    {
        sender = null,
        senderUser = null,
        text = "",
        replyTo = null,
    } = {}
) => {
    return async (dispatch) => {
        const cleanText = String(text || "").trim();
        if (!chatId || !cleanText) return null;

        const clientMessageId = createClientMessageId();
        const optimisticMessage = {
            _id: `local:${clientMessageId}`,
            __clientMessageId: clientMessageId,
            __optimistic: true,
            __sendStatus: "sending",
            chatId,
            sender: senderUser || sender,
            text: cleanText,
            replyTo: replyTo || null,
            readBy: sender ? [sender] : [],
            createdAt: new Date().toISOString(),
        };

        // Текст появляется в UI сразу. В отличие от файлов этот action намеренно
        // НЕ включает глобальный state.sending: следующий Enter можно нажать
        // немедленно, пока предыдущий POST ещё находится в полёте.
        dispatch({
            type: messageConstants.SEND_MESSAGE_OPTIMISTIC_ADD,
            payload: {
                clientMessageId,
                message: optimisticMessage,
            },
        });

        try {
            const formData = new FormData();
            formData.append("text", cleanText);
            if (sender) formData.append("sender", sender);
            if (replyTo) formData.append("replyTo", JSON.stringify(replyTo));

            const res = await axios.post(`/messages/${chatId}`, formData);
            const message = res?.data?.newMsg || null;

            if (!message) {
                dispatch({
                    type: messageConstants.SEND_MESSAGE_OPTIMISTIC_FAILURE,
                    payload: {
                        clientMessageId,
                        error: "Сообщение не создано",
                    },
                });
                return null;
            }

            dispatch({
                type: messageConstants.SEND_MESSAGE_OPTIMISTIC_CONFIRM,
                payload: {
                    clientMessageId,
                    message,
                },
            });

            return message;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_OPTIMISTIC_FAILURE,
                payload: {
                    clientMessageId,
                    error: error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        error.message ||
                        "Не удалось отправить сообщение",
                },
            });

            return null;
        }
    };
};

export const sendFileMessage = (chatId, { sender = null, text = "", replyTo = null } = {}, file) => {
    return async (dispatch) => {
        return dispatch(sendMessage(chatId, { sender, text, replyTo }, file));
    };
};

export const sendVoiceMessage = (chatId, { sender = null, durationMs = 0, replyTo = null } = {}, blob) => {
    return async (dispatch) => {
        if (!blob || !blob.size) return null;

        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        const mediaDurationSec = Math.max(0, Math.round((Number(durationMs) || 0) / 1000));

        return dispatch(sendMessage(chatId, { sender, text: "", mediaDurationSec, replyTo }, file));
    };
};

export const shareProductToChat = (chatId, productId) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-product`, { productId });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: "Товар не отправлен" } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const shareOrderToChat = (chatId, orderId) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-order`, { orderId });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: "Заказ не отправлен" } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const shareEntityToChat = (chatId, {
    type,
    entityId,
    officeId,
    text = "",
} = {}) => {
    return async (dispatch) => {
        if (!chatId || !type || !entityId) return null;

        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-entity`, {
                type,
                entityId,
                officeId,
                text,
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({
                    type: messageConstants.SEND_MESSAGE_SUCCESS,
                    payload: { message },
                });
                return message;
            }

            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: "Объект не отправлен" },
            });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: {
                    error: error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        error.message ||
                        "Не удалось отправить объект",
                },
            });
            return null;
        }
    };
};

export const sharePaymentReceiptToChat = (chatId, { paymentAccountId, receiptId, text = '' } = {}) => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-payment-receipt`, {
                paymentAccountId,
                receiptId,
                text,
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: 'Чек не отправлен' } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });
            throw error;
        }
    };
};

export const shareSceneScreenshotToChat = (chatId, screenshotId, text = '') => {
    return async (dispatch) => {
        dispatch({ type: messageConstants.SEND_MESSAGE_REQUEST });

        try {
            const res = await axios.post(`/chats/${chatId}/share-scene-screenshot`, {
                screenshotId,
                text,
            });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({ type: messageConstants.SEND_MESSAGE_SUCCESS, payload: { message } });
                return message;
            }

            dispatch({ type: messageConstants.SEND_MESSAGE_FAILURE, payload: { error: 'Снимок не отправлен' } });
            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.SEND_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });
            throw error;
        }
    };
};

export const getMessagesByChat = (chatId, options = {}) => {
    return async (dispatch) => {
        const {
            before = null,
            limit = 50,
            append = false,
        } = options;

        const requestType = append
            ? messageConstants.GET_MESSAGES_OLDER_REQUEST
            : messageConstants.GET_MESSAGES_REQUEST;

        const failureType = append
            ? messageConstants.GET_MESSAGES_OLDER_FAILURE
            : messageConstants.GET_MESSAGES_FAILURE;

        dispatch({
            type: requestType,
            payload: { chatId },
        });

        try {
            const params = new URLSearchParams();

            params.set("limit", String(limit));

            if (before) params.set("before", before);

            const res = await axios.get(`/chat/${chatId}/timeline?${params.toString()}`);
            const items = normalizeTimelineItems(res?.data?.items || [], chatId);

            dispatch({
                type: messageConstants.GET_MESSAGES_SUCCESS,
                payload: {
                    chatId,
                    messages: items,
                    nextCursor: res?.data?.nextCursor || null,
                    hasMore: !!res?.data?.nextCursor,
                    append,
                },
            });

            return {
                messages: items,
                nextCursor: res?.data?.nextCursor || null,
                hasMore: !!res?.data?.nextCursor,
            };
        } catch (error) {
            const status = error?.response?.status;

            if (!append && status === 404) {
                try {
                    const fallbackRes = await axios.get(`/messages/getMessagesByChat/${chatId}`);
                    const fallbackMessages = Array.isArray(fallbackRes?.data?.messages)
                        ? fallbackRes.data.messages.map((message) => ({ ...message, chatId: message.chatId || chatId }))
                        : [];

                    dispatch({
                        type: messageConstants.GET_MESSAGES_SUCCESS,
                        payload: {
                            chatId,
                            messages: fallbackMessages,
                            nextCursor: null,
                            hasMore: false,
                            append: false,
                        },
                    });

                    return {
                        messages: fallbackMessages,
                        nextCursor: null,
                        hasMore: false,
                    };
                } catch (fallbackError) {
                    dispatch({
                        type: failureType,
                        payload: { chatId, error: fallbackError?.response?.data?.error || fallbackError.message },
                    });

                    return {
                        messages: [],
                        nextCursor: null,
                        hasMore: false,
                    };
                }
            }

            if (status === 403 || status === 404) {
                dispatch({
                    type: chatConstants.REMOVE_CHAT,
                    payload: { chatId },
                });
            }

            dispatch({
                type: failureType,
                payload: { chatId, error: error?.response?.data?.error || error.message },
            });

            return {
                messages: [],
                nextCursor: null,
                hasMore: false,
            };
        }
    };
};

export const markMessagesAsRead = (chatId, userId = null) => {
    return async (dispatch, getState) => {
        const currentUserId = userId || getState().auth?.user?._id || getState().auth?.user?.id;

        if (!chatId || !currentUserId) return false;

        dispatch({ type: messageConstants.MARK_MESSAGES_AS_READ_REQUEST });

        try {
            const res = await axios.patch("/messages/mark-read", { chatId, userId: currentUserId });

            if (res.status === 200) {
                dispatch({
                    type: messageConstants.MARK_MESSAGES_AS_READ_SUCCESS,
                    payload: { chatId, userId: currentUserId },
                });

                return true;
            }

            dispatch({ type: messageConstants.MARK_MESSAGES_AS_READ_FAILURE });

            return false;
        } catch (error) {
            dispatch({
                type: messageConstants.MARK_MESSAGES_AS_READ_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return false;
        }
    };
};

export const deleteMessage = (messageId, chatId, source = "user") => {
    return async (dispatch, getState) => {
        if (source === "socket") {
            dispatch({ type: messageConstants.DELETE_MESSAGE_SUCCESS, payload: { messageId } });
            return true;
        }

        dispatch({ type: messageConstants.DELETE_MESSAGE_REQUEST, payload: { messageId } });

        try {
            const res = await axios.delete(`/messages/${messageId}`);

            if (res.status === 200) {
                dispatch({ type: messageConstants.DELETE_MESSAGE_SUCCESS, payload: { messageId } });

                return true;
            }

            dispatch({ type: messageConstants.DELETE_MESSAGE_FAILURE, payload: { messageId } });

            return false;
        } catch (error) {
            dispatch({
                type: messageConstants.DELETE_MESSAGE_FAILURE,
                payload: { messageId, error: error?.response?.data?.error || error.message },
            });

            return false;
        }
    };
};

export const forwardMessage = (messageId, targetChatId) => {
    return async (dispatch) => {
        dispatch({
            type: messageConstants.FORWARD_MESSAGE_REQUEST,
            payload: { messageId, targetChatId },
        });

        try {
            const res = await axios.post(`/messages/${messageId}/forward`, { targetChatId });
            const message = res?.data?.newMsg || null;

            if (message) {
                dispatch({
                    type: messageConstants.FORWARD_MESSAGE_SUCCESS,
                    payload: { message },
                });


                return message;
            }

            dispatch({
                type: messageConstants.FORWARD_MESSAGE_FAILURE,
                payload: { error: "Сообщение не переслано" },
            });

            return null;
        } catch (error) {
            dispatch({
                type: messageConstants.FORWARD_MESSAGE_FAILURE,
                payload: { error: error?.response?.data?.error || error?.response?.data?.message || error.message },
            });

            return null;
        }
    };
};

export const forwardMessagesBulk = (messageIds = [], targetChatIds = []) => {
    return async (dispatch) => {
        const sourceIds = Array.from(new Set((messageIds || []).map(getId).filter(Boolean))).slice(0, 100);
        const targetIds = Array.from(new Set((targetChatIds || []).map(getId).filter(Boolean))).slice(0, 10);

        if (!sourceIds.length || !targetIds.length) return null;

        dispatch({
            type: messageConstants.FORWARD_MESSAGE_REQUEST,
            payload: { messageIds: sourceIds, targetChatIds: targetIds },
        });

        try {
            const res = await axios.post("/messages/forward-bulk", {
                messageIds: sourceIds,
                targetChatIds: targetIds,
            });

            const createdMessages = Array.isArray(res?.data?.messages)
                ? res.data.messages
                : [];

            dispatch({
                type: messageConstants.FORWARD_MESSAGE_SUCCESS,
                payload: {
                    messages: createdMessages,
                    result: res?.data || null,
                },
            });

            return res?.data || null;
        } catch (error) {
            dispatch({
                type: messageConstants.FORWARD_MESSAGE_FAILURE,
                payload: {
                    error: error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        error.message ||
                        "Не удалось переслать сообщения",
                },
            });

            return null;
        }
    };
};

export const toggleMessageStar = (messageId, on) => {
    return async (dispatch) => {
        try {
            const res = await axios.post(`/messages/${messageId}/star`, { on });

            if (res.status === 200) {
                dispatch({
                    type: messageConstants.RECEIVE_NEW_MESSAGE,
                    payload: {
                        message: {
                            _id: messageId,
                            __starPatch: true,
                            on: !!on,
                            starredBy: res.data?.starredBy || [],
                        },
                    },
                });
            }

            return res.data;
        } catch (_error) {
            return null;
        }
    };
};

export const clearMessages = () => ({ type: messageConstants.CLEAR_MESSAGES });

export const clearAdminMessages = clearMessages;

export const receiveMessage = (message, meId = null) => ({
    type: messageConstants.RECEIVE_NEW_MESSAGE,
    payload: { message, meId },
});

export const receiveAdminMessage = receiveMessage;

export const deleteMessageSocket = (messageId) => ({
    type: messageConstants.DELETE_MESSAGE_SUCCESS,
    payload: { messageId },
});

export const deleteMessageLocal = deleteMessageSocket;

export const markMessagesAsReadSocket = (chatId, userId) => ({
    type: messageConstants.MARK_MESSAGES_AS_READ_SUCCESS,
    payload: { chatId, userId },
});
